import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Box,
  List,
  ListItem,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TableCell,
  TableRow,
  TableBody,
  TableContainer,
  Table,
} from "@mui/material";
import { Formik, Form, Field } from "formik";
import Swal from "sweetalert2";
import { loanManagementUrl, setupManagementUrl } from "../../config";
import { useNavigate } from "react-router-dom";
import {
  executeAnyMutation,
  fetchJson,
  fetchJsonDynamic,
} from "../../data/DynamicServiceNew/jsondynamicservice";
import { Bold } from "lucide-react";

const LoanCalculator = () => {
  const [loanProduct, setLoanProduct] = useState([]);
  const [result, setResult] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));
  const companyId = parseInt(decodedToken.CompanyId);

  useEffect(() => {
    const fetchData = async () => {
      const params = { companyId };
      const productResponse = await fetchJsonDynamic(
        "allProductsByColumnValues",
        "parameters",
        params,
        setupManagementUrl.uri
      );
      const allProducts = JSON.parse(productResponse.results) || [];
      setLoanProduct(allProducts);
    };
    fetchData();
  }, []);

  const handleOnSubmit = async (values, { setSubmitting }) => {
    try {
      const params = {
        productId: parseInt(values.productId),
        amount: parseFloat(values.amount),
      };

      const response = await fetchJsonDynamic(
        "loanCalculator",
        "input",
        params,
        loanManagementUrl.uri
      );

      const parsedResults = JSON.parse(response.results);
      if (Array.isArray(parsedResults)) {
        setResult(parsedResults);
      } else {
        Swal.fire({
          icon: "error",
          title: "Error!",
          text: parsedResults || "Unexpected response format.",
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "An error occurred while processing your request.",
      });
    } finally {
      setSubmitting(false);
    }
  };
  const handleProductChange = (event) => {
    const selectedProduct = loanProduct.find((product) => product.Id === event);
    if (selectedProduct) {
      setSelectedProduct(selectedProduct); // Set the selected product
    } else {
      setSelectedProduct(null); // Clear if not found
    }
    // Optional: If you're tracking productId in a form state
    // setValues((prev) => ({
    //   ...prev,
    //   productId: productId,
    // }));
  };

  return (
    <Container maxWidth="xl">
      <Paper elevation={4} sx={{ p: 4, mt: 5 }}>
        <Typography variant="h5" sx={{ fontWeight: "bold" }} gutterBottom>
          Loan Calculator
        </Typography>

        <Formik
          initialValues={{ productId: "", amount: "" }}
          onSubmit={handleOnSubmit}
        >
          {({ values, handleChange, isSubmitting }) => (
            <Form>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel id="product-select-label">
                      Select Product
                    </InputLabel>
                    <Select
                      labelId="product-select-label"
                      id="productId"
                      name="productId"
                      value={values.productId}
                      onChange={(e) => {
                        handleChange(e);
                        handleProductChange(e.target.value);
                      }}
                      required
                    >
                      {loanProduct &&
                        loanProduct.map((product) => (
                          <MenuItem key={product.Id} value={product.Id}>
                            {product.Name}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    id="amount"
                    name="amount"
                    label="Amount Disbursed"
                    type="number"
                    value={values.amount}
                    onChange={handleChange}
                    margin="normal"
                    required
                  />
                </Grid>

                <Grid item xs={12} md={4} display="flex" alignItems="center">
                  <Box mt={2} width="100%">
                    <Button
                      variant="contained"
                      type="submit"
                      fullWidth
                      disabled={isSubmitting}
                    >
                      Calculate
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>

        {result &&
          result.map((item, idx) => (
            <Box key={idx} mt={4}>
              <Typography variant="h6" sx={{ fontWeight: "bold" }} gutterBottom>
                Loan Details
              </Typography>

              <TableContainer component={Paper}>
                <Table>
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ borderRight: "1px solid #ddd" }}>
                        <strong>Interest:</strong>
                      </TableCell>
                      <TableCell sx={{ borderRight: "1px solid #ddd" }}>
                        {parseFloat(item.Interest).toFixed(2)}
                      </TableCell>
                      <TableCell sx={{ borderRight: "1px solid #ddd" }}>
                        <strong>Loan Amount:</strong>
                      </TableCell>
                      <TableCell sx={{ borderRight: "1px solid #ddd" }}>
                        {parseFloat(item.Loan_amount).toFixed(2)}
                      </TableCell>
                      <TableCell sx={{ borderRight: "1px solid #ddd" }}>
                        <strong>Deposit:</strong>
                      </TableCell>
                      <TableCell sx={{ borderRight: "1px solid #ddd" }}>
                        {parseFloat(item.Deposit).toFixed(2)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ borderRight: "1px solid #ddd" }}>
                        <strong>Total Charges:</strong>
                      </TableCell>
                      <TableCell sx={{ borderRight: "1px solid #ddd" }}>
                        {parseFloat(item.Total_charges).toFixed(2)}
                      </TableCell>

                      <TableCell sx={{ borderRight: "1px solid #ddd" }}>
                        <strong>Total Payable:</strong>
                      </TableCell>
                      <TableCell sx={{ borderRight: "1px solid #ddd" }}>
                        <Typography fontWeight="bold" color="primary">
                          {parseFloat(item.Total_payable).toFixed(2)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
              {selectedProduct && (
                <Box mt={4} width="100%">
                  <Typography variant="h6" sx={{ fontWeight: "bold" }} gutterBottom>
                    Loan Schedule
                  </Typography>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableBody>
                        <TableRow>
                          <TableCell sx={{ borderRight: "1px solid #ddd" }}>
                            <strong>No of Installments:</strong>
                          </TableCell>
                          <TableCell sx={{ borderRight: "1px solid #ddd" }}>
                            {parseFloat(
                              selectedProduct.NoOfInstallments
                            ).toFixed(2)}
                          </TableCell>
                          <TableCell sx={{ borderRight: "1px solid #ddd" }}>
                            <strong>Interest per installment:</strong>
                          </TableCell>
                          <TableCell sx={{ borderRight: "1px solid #ddd" }}>
                            {parseFloat(
                              item.Interest / selectedProduct.NoOfInstallments
                            ).toFixed(2)}
                          </TableCell>
                          <TableCell sx={{ borderRight: "1px solid #ddd" }}>
                            <strong>Total Amount per installment:</strong>
                          </TableCell>
                          <TableCell sx={{ borderRight: "1px solid #ddd" }}>
                            <Typography fontWeight="bold" color="primary">
                              {parseFloat(
                                item.Loan_amount /
                                  selectedProduct.NoOfInstallments
                              ).toFixed(2)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Box>
          ))}
      </Paper>
    </Container>
  );
};

export default LoanCalculator;
